import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Alert,
  CircularProgress,
  Divider,
  Avatar,
  Tooltip,
  Switch,
  FormControlLabel,
  InputAdornment,
  Card,
  CardContent,
  Grid,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Visibility,
  WhatsApp,
  Save,
  Cancel,
  ContentCopy,
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import {
  getWhatsAppTemplates,
  createWhatsAppTemplate,
  updateWhatsAppTemplate,
  deleteWhatsAppTemplate
} from '../../redux/actions/communicationActions';

const WhatsAppTemplateManager = () => {
  const dispatch = useDispatch();
  const { whatsappTemplates, loading, error } = useSelector((state) => state.communication || {});
  
  const [openDialog, setOpenDialog] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templateData, setTemplateData] = useState({
    name: '',
    template_id: '',
    message: '',
    type: 'general',
    is_active: true,
    variables: []
  });
  const [previewDialog, setPreviewDialog] = useState(false);
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');

  useEffect(() => {
    dispatch(getWhatsAppTemplates());
  }, [dispatch]);

  const handleOpenDialog = (template = null) => {
    if (template) {
      setEditingTemplate(template);
      setTemplateData({
        name: template.name || '',
        template_id: template.template_id || '',
        message: template.message || '',
        type: template.type || 'general',
        is_active: template.is_active !== undefined ? template.is_active : true,
        variables: template.variables || []
      });
    } else {
      setEditingTemplate(null);
      setTemplateData({
        name: '',
        template_id: '',
        message: '',
        type: 'general',
        is_active: true,
        variables: []
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingTemplate(null);
    setTemplateData({
      name: '',
      template_id: '',
      message: '',
      type: 'general',
      is_active: true,
      variables: []
    });
  };

  const handleSaveTemplate = async () => {
    if (editingTemplate) {
      const result = await dispatch(updateWhatsAppTemplate(editingTemplate.id, templateData));
      if (result.success) {
        handleCloseDialog();
        dispatch(getWhatsAppTemplates());
      }
    } else {
      const result = await dispatch(createWhatsAppTemplate(templateData));
      if (result.success) {
        handleCloseDialog();
        dispatch(getWhatsAppTemplates());
      }
    }
  };

  const handleDeleteTemplate = async (id) => {
    if (window.confirm('Are you sure you want to delete this template? This action cannot be undone.')) {
      const result = await dispatch(deleteWhatsAppTemplate(id));
      if (result.success) {
        dispatch(getWhatsAppTemplates());
      }
    }
  };

  const handlePreviewTemplate = (template) => {
    setPreviewTemplate(template);
    setPreviewDialog(true);
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'invoice': return 'primary';
      case 'offer': return 'success';
      case 'birthday': return 'warning';
      case 'general': return 'default';
      default: return 'default';
    }
  };

  const templateTypes = [
    { value: 'invoice', label: 'Invoice' },
    { value: 'offer', label: 'Offer' },
    { value: 'birthday', label: 'Birthday' },
    { value: 'general', label: 'General' }
  ];

  const getAvailableVariables = (type) => {
    switch (type) {
      case 'invoice':
        return '{customer_name}, {invoice_number}, {invoice_date}, {total_amount}, {company_name}';
      case 'birthday':
        return '{customer_name}, {discount_percentage}, {company_name}, {valid_until}';
      case 'offer':
        return '{customer_name}, {offer_description}, {offer_end_date}, {company_name}';
      default:
        return '{customer_name}, {company_name}';
    }
  };

  const filteredTemplates = whatsappTemplates.filter(template => {
    const matchesSearch = template.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         template.template_id?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = typeFilter === 'all' || template.type === typeFilter;
    return matchesSearch && matchesType;
  });

  if (loading && whatsappTemplates.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h6" fontWeight="bold" gutterBottom>
            WhatsApp Templates
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Create and manage WhatsApp message templates for customer communications
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => handleOpenDialog()}
          sx={{ 
            borderRadius: 2,
            textTransform: 'none',
            px: 3,
            background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
            '&:hover': {
              background: 'linear-gradient(135deg, #0d7a6f 0%, #2dd169 100%)',
            }
          }}
        >
          Add Template
        </Button>
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 3, boxShadow: 2 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search templates..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Visibility />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Filter by Type</InputLabel>
                <Select
                  value={typeFilter}
                  onChange={(e) => setTypeFilter(e.target.value)}
                  label="Filter by Type"
                >
                  <MenuItem value="all">All Types</MenuItem>
                  {templateTypes.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Templates Table */}
      <TableContainer component={Paper} sx={{ boxShadow: 2, borderRadius: 2 }}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: 'grey.50' }}>
              <TableCell sx={{ fontWeight: 'bold' }}>Template Name</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Type</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Template ID</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Created</TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredTemplates.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    No templates found. Create your first template to get started.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredTemplates.map((template) => (
                <TableRow 
                  key={template.id}
                  sx={{ 
                    '&:hover': { bgcolor: 'action.hover' },
                    '&:last-child td': { border: 0 }
                  }}
                >
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Avatar sx={{ bgcolor: 'success.main', width: 32, height: 32 }}>
                        <WhatsApp sx={{ fontSize: 18 }} />
                      </Avatar>
                      <Typography variant="body2" fontWeight="medium">
                        {template.name}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={template.type}
                      color={getTypeColor(template.type)}
                      size="small"
                      sx={{ textTransform: 'capitalize' }}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontFamily="monospace" sx={{ fontSize: '0.75rem' }}>
                      {template.template_id}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={template.is_active ? 'Active' : 'Inactive'}
                      color={template.is_active ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {new Date(template.created_at).toLocaleDateString()}
                  </TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                      <Tooltip title="Preview">
                        <IconButton
                          size="small"
                          onClick={() => handlePreviewTemplate(template)}
                          color="info"
                        >
                          <Visibility fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit">
                        <IconButton
                          size="small"
                          onClick={() => handleOpenDialog(template)}
                          color="primary"
                        >
                          <Edit fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteTemplate(template.id)}
                          color="error"
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Template Dialog */}
      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog} 
        maxWidth="lg" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 12px 48px rgba(0,0,0,0.15)',
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle sx={{ 
          background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          py: 2.5,
          px: 3
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 40, height: 40 }}>
              <WhatsApp sx={{ fontSize: 24 }} />
            </Avatar>
            <Box>
              <Typography variant="h6" fontWeight="bold">
                {editingTemplate ? 'Edit WhatsApp Template' : 'Create WhatsApp Template'}
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.9 }}>
                {editingTemplate ? 'Update your WhatsApp template details' : 'Design a new WhatsApp message template for customer communications'}
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 4, px: 3, pb: 2 }}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Card variant="outlined" sx={{ p: 2, bgcolor: 'grey.50', border: '2px dashed', borderColor: 'success.light' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                  <Avatar sx={{ bgcolor: 'success.main', width: 36, height: 36 }}>
                    <ContentCopy sx={{ fontSize: 20 }} />
                  </Avatar>
                  <Box>
                    <Typography variant="subtitle2" fontWeight="bold" color="success.dark">
                      Template Information
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Basic details about your WhatsApp template
                    </Typography>
                  </Box>
                </Box>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Template Name"
                      value={templateData.name}
                      onChange={(e) => setTemplateData(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="e.g., Invoice WhatsApp Template"
                      required
                      variant="outlined"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          bgcolor: 'white',
                          '&:hover': { bgcolor: 'grey.50' }
                        }
                      }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <ContentCopy color="action" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel>Template Type</InputLabel>
                      <Select
                        value={templateData.type}
                        onChange={(e) => setTemplateData(prev => ({ ...prev, type: e.target.value }))}
                        label="Template Type"
                        sx={{
                          bgcolor: 'white',
                          '&:hover': { bgcolor: 'grey.50' }
                        }}
                      >
                        {templateTypes.map((type) => (
                          <MenuItem key={type.value} value={type.value}>
                            <Chip 
                              label={type.label} 
                              size="small" 
                              color={getTypeColor(type.value)}
                              sx={{ mr: 1 }}
                            />
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </Card>
            </Grid>

            <Grid item xs={12}>
              <Card variant="outlined" sx={{ p: 2, bgcolor: 'grey.50', border: '2px dashed', borderColor: 'success.light' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                  <Avatar sx={{ bgcolor: 'success.main', width: 36, height: 36 }}>
                    <WhatsApp sx={{ fontSize: 20 }} />
                  </Avatar>
                  <Box>
                    <Typography variant="subtitle2" fontWeight="bold" color="success.dark">
                      WhatsApp Template Details
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Template ID and message content for WhatsApp
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <TextField
                    fullWidth
                    label="WhatsApp Template ID"
                    value={templateData.template_id}
                    onChange={(e) => setTemplateData(prev => ({ ...prev, template_id: e.target.value }))}
                    placeholder="e.g., invoice_notification_001"
                    required
                    variant="outlined"
                    helperText={
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          This should match the template ID in your WhatsApp Business account
                        </Typography>
                        <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 0.5 }}>
                          ⚠️ Ensure the template ID is approved by WhatsApp before using
                        </Typography>
                      </Box>
                    }
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        bgcolor: 'white',
                        fontFamily: 'monospace',
                        fontSize: '0.875rem',
                        '&:hover': { bgcolor: 'grey.50' }
                      }
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <WhatsApp color="action" />
                        </InputAdornment>
                      ),
                    }}
                  />

                  <TextField
                    fullWidth
                    label="Message Template"
                    multiline
                    rows={12}
                    value={templateData.message}
                    onChange={(e) => setTemplateData(prev => ({ ...prev, message: e.target.value }))}
                    placeholder="Enter your WhatsApp message template here. Use variables like {customer_name}, {invoice_number}, etc."
                    required
                    variant="outlined"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        bgcolor: 'white',
                        '&:hover': { bgcolor: 'grey.50' },
                        fontFamily: 'monospace',
                        fontSize: '0.875rem'
                      }
                    }}
                  />
                </Box>
              </Card>
            </Grid>

            <Grid item xs={12}>
              <Card variant="outlined" sx={{ p: 2, bgcolor: 'success.light', border: '2px solid', borderColor: 'success.main' }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                  <Avatar sx={{ bgcolor: 'success.main', width: 36, height: 36, mt: 0.5 }}>
                    <Visibility sx={{ fontSize: 20 }} />
                  </Avatar>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle2" fontWeight="bold" gutterBottom sx={{ color: 'success.dark' }}>
                      Available Variables:
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {getAvailableVariables(templateData.type).split(', ').map((variable, index) => (
                        <Chip
                          key={index}
                          label={variable}
                          size="small"
                          sx={{
                            bgcolor: 'white',
                            color: 'success.dark',
                            fontWeight: 500,
                            fontFamily: 'monospace',
                            fontSize: '0.75rem',
                            '&:hover': {
                              bgcolor: 'success.main',
                              color: 'white'
                            }
                          }}
                        />
                      ))}
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                      Click on variables to copy them or type them directly in the template
                    </Typography>
                  </Box>
                </Box>
              </Card>
            </Grid>

            <Grid item xs={12}>
              <Card variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={templateData.is_active}
                      onChange={(e) => setTemplateData(prev => ({ ...prev, is_active: e.target.checked }))}
                      color="success"
                      size="medium"
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="subtitle2" fontWeight="bold">
                        Active Template
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Only active templates can be used in campaigns
                      </Typography>
                    </Box>
                  }
                />
              </Card>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, pt: 2, bgcolor: 'grey.50' }}>
          <Button 
            onClick={handleCloseDialog}
            variant="outlined"
            startIcon={<Cancel />}
            sx={{ 
              borderRadius: 2, 
              textTransform: 'none',
              px: 3,
              borderColor: 'grey.400',
              color: 'text.primary',
              '&:hover': {
                borderColor: 'grey.600',
                bgcolor: 'grey.100'
              }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveTemplate}
            variant="contained"
            disabled={!templateData.name || !templateData.template_id || !templateData.message}
            startIcon={<Save />}
            sx={{ 
              borderRadius: 2, 
              textTransform: 'none',
              px: 4,
              background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #0d7a6f 0%, #2dd169 100%)',
              },
              '&:disabled': {
                background: 'grey.300'
              }
            }}
          >
            {editingTemplate ? 'Update Template' : 'Create Template'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog 
        open={previewDialog} 
        onClose={() => setPreviewDialog(false)} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
          }
        }}
      >
        <DialogTitle sx={{ 
          bgcolor: 'success.main',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}>
          <Visibility />
          <Typography variant="h6" fontWeight="bold">
            Template Preview
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {previewTemplate && (
            <Box>
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  {previewTemplate.name}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                  <Chip
                    label={previewTemplate.type}
                    color={getTypeColor(previewTemplate.type)}
                    size="small"
                  />
                  <Chip
                    label={previewTemplate.is_active ? 'Active' : 'Inactive'}
                    color={previewTemplate.is_active ? 'success' : 'default'}
                    size="small"
                  />
                </Box>
                <Divider />
              </Box>
              
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Template ID:
              </Typography>
              <Typography variant="body1" gutterBottom sx={{ mb: 3, fontFamily: 'monospace', fontSize: '0.875rem' }}>
                {previewTemplate.template_id}
              </Typography>

              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Message:
              </Typography>
              <Box
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  p: 2,
                  bgcolor: 'grey.50',
                  maxHeight: 400,
                  overflow: 'auto',
                  fontFamily: 'monospace',
                  fontSize: '0.875rem',
                  whiteSpace: 'pre-wrap'
                }}
              >
                {previewTemplate.message}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button 
            onClick={() => setPreviewDialog(false)}
            variant="contained"
            color="success"
            sx={{ borderRadius: 2, textTransform: 'none' }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WhatsAppTemplateManager;
